/* eslint-disable */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { bannersApi } from '../lib/api';

// Map link_type + link_value -> URL handler
const resolveBannerHref = (banner) => {
  const t = banner.link_type;
  const v = (banner.link_value || '').trim();
  if (!v && t !== 'none') return null;
  switch (t) {
    case 'product': return { internal: true, href: `/product/${v}` };
    case 'collection': return { internal: true, href: `/shop?category=${encodeURIComponent(v)}` };
    case 'cms': return { internal: true, href: v.startsWith('/') ? v : `/${v}` };
    case 'url': return { internal: /^https?:\/\//i.test(v) ? false : true, href: v };
    case 'none':
    default:
      return null;
  }
};

const BannerImage = ({ banner }) => (
  <picture>
    {banner.image_mobile && <source media="(max-width: 640px)" srcSet={banner.image_mobile} />}
    {banner.image_tablet && <source media="(max-width: 1024px)" srcSet={banner.image_tablet} />}
    <img
      src={banner.image_desktop}
      alt={banner.title || 'Banner'}
      className="w-full h-full object-cover select-none"
      draggable={false}
      loading="eager"
    />
  </picture>
);

const HeroBannerSlider = () => {
  const [banners, setBanners] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'start' });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    bannersApi.list('hero')
      .then((b) => setBanners(b))
      .catch(() => setBanners([]))
      .finally(() => setLoaded(true));
  }, []);

  const onSelect = useCallback((api) => {
    if (api) setSelectedIndex(api.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect(emblaApi);
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  // Autoplay (only when multiple banners)
  useEffect(() => {
    if (!emblaApi || banners.length < 2) return;
    const id = setInterval(() => {
      try { emblaApi.scrollNext(); } catch {}
    }, 6000);
    return () => clearInterval(id);
  }, [emblaApi, banners.length]);

  if (!loaded) {
    return <div className="w-full bg-[#ece4d2] velouva-banner-ratio" />;
  }
  if (banners.length === 0) return null;

  const onBannerClick = (banner) => {
    const link = resolveBannerHref(banner);
    if (!link) return;
    if (link.internal) {
      navigate(link.href);
    } else if (banner.open_in_new_tab) {
      window.open(link.href, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = link.href;
    }
  };

  const renderBannerSlide = (b, isStatic = false) => {
    const link = resolveBannerHref(b);
    const inner = (
      <div className="relative w-full h-full">
        <BannerImage banner={b} />
        {(b.title || b.subtitle) && (
          <div className="absolute inset-0 flex items-end">
            <div className="w-full bg-gradient-to-t from-black/40 to-transparent text-white p-6 md:p-10 lg:p-14">
              {b.subtitle && <div className="text-[11px] tracking-[0.32em] uppercase opacity-90">{b.subtitle}</div>}
              {b.title && <div className="font-serif text-2xl md:text-4xl lg:text-5xl mt-2 max-w-2xl leading-tight">{b.title}</div>}
            </div>
          </div>
        )}
      </div>
    );
    if (!link) {
      return <div className={`w-full ${isStatic ? '' : 'velouva-banner-ratio'}`}>{inner}</div>;
    }
    return (
      <button
        type="button"
        onClick={() => onBannerClick(b)}
        className={`w-full ${isStatic ? '' : 'velouva-banner-ratio'} text-left cursor-pointer`}
        aria-label={b.title || 'Open banner link'}
      >
        {inner}
      </button>
    );
  };

  // Single banner — static, no controls
  if (banners.length === 1) {
    return (
      <section className="w-full velouva-banner-ratio overflow-hidden bg-[#ece4d2]">
        {renderBannerSlide(banners[0], true)}
      </section>
    );
  }

  return (
    <section className="relative w-full overflow-hidden bg-[#ece4d2]">
      <div ref={emblaRef} className="overflow-hidden">
        <div className="flex">
          {banners.map((b) => (
            <div key={b.id} className="flex-[0_0_100%] min-w-0 velouva-banner-ratio">
              {renderBannerSlide(b, true)}
            </div>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={() => emblaApi && emblaApi.scrollPrev()}
        aria-label="Previous banner"
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/85 hover:bg-white items-center justify-center transition-colors"
      >
        <ChevronLeft className="w-6 h-6 text-[#1a1410]" strokeWidth={1.4} />
      </button>
      <button
        type="button"
        onClick={() => emblaApi && emblaApi.scrollNext()}
        aria-label="Next banner"
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/85 hover:bg-white items-center justify-center transition-colors"
      >
        <ChevronRight className="w-6 h-6 text-[#1a1410]" strokeWidth={1.4} />
      </button>

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {banners.map((_, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => emblaApi && emblaApi.scrollTo(idx)}
            aria-label={`Go to banner ${idx + 1}`}
            className={`h-1.5 rounded-full transition-all ${selectedIndex === idx ? 'w-8 bg-white' : 'w-1.5 bg-white/60 hover:bg-white/90'}`}
          />
        ))}
      </div>
    </section>
  );
};

export default HeroBannerSlider;
